// Label printing (UI design §3.8; the Settings → Labels page until 2026-09-15): open to every role, for the assets
// and zones the viewer can see (RLS scopes both). Asset labels print from the Assets list (tick, Print labels); zone
// signs from a place's Assets tab or its Zones dialog — the app scans a sign for put-away mode (CR_20260914).
// Pick a layout, download PDF / ZPL / engraver CSV; the preview shows the first label's QR exactly as printed.
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../auth/session';
import { activeZones, useZones } from '../data/zones';
import i18n from '../i18n';
import {
  download,
  type LabelItem,
  labelsCsv,
  labelsPdf,
  labelsZpl,
  labelUrl,
  type Layout,
  qrMatrix,
  unprintable,
  type ZoneLabelItem,
  zoneLabelsCsv,
  zoneLabelsPdf,
  zoneLabelsZpl,
  zoneLabelUrl,
  type ZoneLayout,
} from '../lib/labels';
import { appConfig } from '../lib/supabase';
import { Loading, LoadError } from './Page';

function QrPreview({ url }: { url: string }) {
  const matrix = useMemo(() => qrMatrix(url), [url]);
  const n = matrix.length + 4;
  return (
    <svg
      viewBox={`0 0 ${n} ${n}`}
      width={132}
      height={132}
      role="img"
      aria-label={url}
      data-testid="label-preview"
      shapeRendering="crispEdges"
    >
      <rect width={n} height={n} fill="#fff" />
      {matrix.flatMap((row, r) =>
        row.map((dark, c) =>
          dark ? (
            <rect key={`${r}-${c}`} x={c + 2} y={r + 2} width={1} height={1} fill="#000" />
          ) : null,
        ),
      )}
    </svg>
  );
}

/** The first label as printed: its QR beside its text lines. */
function Preview({ url, lines }: { url: string; lines: ReactNode }) {
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{ border: 1, borderColor: 'divider', p: 1.5, borderRadius: 1, alignSelf: 'flex-start' }}
    >
      <QrPreview url={url} />
      <Box>{lines}</Box>
    </Stack>
  );
}

/** Layout, count and the three downloads, with a preview of the first label; `children` go above the layout. */
function PrintDialog({
  title,
  layouts,
  layout,
  onLayout,
  selectedText,
  count,
  onPdf,
  onZpl,
  onCsv,
  preview,
  onClose,
  children,
}: {
  title: string;
  layouts: { value: string; label: string }[];
  layout: string;
  onLayout(v: string): void;
  selectedText: string;
  count: number;
  onPdf(): Promise<void>;
  onZpl(): void;
  onCsv(): void;
  preview: ReactNode;
  onClose(): void;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const pdf = async () => {
    setBusy(true);
    try {
      await onPdf();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" data-testid="print-labels">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }}>
          <Stack spacing={2} sx={{ flexGrow: 1, minWidth: 0 }}>
            {children}
            <TextField
              select
              label={t('labels.layout')}
              value={layout}
              onChange={(e) => onLayout(e.target.value)}
              sx={{ maxWidth: 360 }}
            >
              {layouts.map((l) => (
                <MenuItem key={l.value} value={l.value}>
                  {l.label}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="body2" color="text.secondary">
              {selectedText}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button variant="contained" disabled={!count || busy} onClick={() => void pdf()}>
                {t('labels.download_pdf')}
              </Button>
              <Button disabled={!count} onClick={onZpl}>
                {t('labels.download_zpl')}
              </Button>
              <Button disabled={!count} onClick={onCsv}>
                {t('labels.download_csv')}
              </Button>
            </Stack>
          </Stack>
          {preview}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

/** QR labels for the assets ticked on the Assets list. */
export function AssetLabelsDialog({
  assets,
  onClose,
}: {
  assets: { asset_code: string; description: string | null }[];
  onClose(): void;
}) {
  const { t } = useTranslation();
  const { me } = useSession();
  const [layout, setLayout] = useState<Layout>('a4_24');
  const company = me?.company;
  const publicUrl = appConfig().publicUrl;
  const items: LabelItem[] = assets.map((a) => ({
    assetCode: a.asset_code,
    companyCode: company?.code ?? '',
    companyName: company?.name ?? '',
    description: a.description,
  }));
  const file = `labels-${company?.code}`;

  return (
    <PrintDialog
      title={t('labels.title')}
      layouts={[
        { value: 'a4_24', label: t('labels.layout_a4') },
        { value: 'roll_50x25', label: t('labels.layout_roll') },
      ]}
      layout={layout}
      onLayout={(v) => setLayout(v as Layout)}
      selectedText={t('labels.selected', { count: items.length })}
      count={items.length}
      onPdf={async () =>
        download(`${file}.pdf`, await labelsPdf(items, layout, publicUrl), 'application/pdf')
      }
      onZpl={() => download(`${file}.zpl`, labelsZpl(items, publicUrl), 'text/plain')}
      onCsv={() => download(`${file}.csv`, labelsCsv(items, publicUrl), 'text/csv')}
      preview={
        items[0] && company ? (
          <Preview
            url={labelUrl(publicUrl, company.code, items[0].assetCode)}
            lines={
              <>
                <Typography variant="h6">{items[0].assetCode}</Typography>
                <Typography variant="caption">{company.name}</Typography>
              </>
            }
          />
        ) : null
      }
      onClose={onClose}
    />
  );
}

/** The sign's "scan with the app" line: standard PDF fonts print Latin script only, so Chinese falls back to English. */
function signHint(): string {
  const lang = i18n.language === 'ms' ? 'ms' : 'en';
  return i18n.getFixedT(lang)('labels.sign_hint');
}

/** A sign per active zone of one place, all ticked until someone unticks one. */
export function ZoneSignsDialog({
  siteId,
  siteName,
  onClose,
}: {
  siteId: string;
  siteName: string;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const { me } = useSession();
  const zones = useZones(siteId);
  const [unticked, setUnticked] = useState<Set<string>>(new Set());
  const [layout, setLayout] = useState<ZoneLayout>('sign_a5x2');
  const company = me?.company;
  const publicUrl = appConfig().publicUrl;
  const active = activeZones(zones.data);

  const items: ZoneLabelItem[] = active
    .filter((z) => !unticked.has(z.id))
    .map((z) => ({
      zoneId: z.id,
      zoneName: z.name,
      siteName,
      companyCode: company?.code ?? '',
      companyName: company?.name ?? '',
    }));
  const gaps = items.filter((i) => unprintable(`${i.zoneName} ${i.siteName}`));
  const file = `zones-${company?.code}-${siteName.replace(/[^A-Za-z0-9-]+/g, '_')}`;

  return (
    <PrintDialog
      title={t('labels.zone_signs_title', { site: siteName })}
      layouts={[
        { value: 'sign_a5x2', label: t('labels.layout_sign_a5') },
        { value: 'sign_a4', label: t('labels.layout_sign_a4') },
        { value: 'a4_24', label: t('labels.layout_strip') },
        { value: 'roll_50x25', label: t('labels.layout_roll') },
      ]}
      layout={layout}
      onLayout={(v) => setLayout(v as ZoneLayout)}
      selectedText={t('labels.zones_selected', { count: items.length })}
      count={items.length}
      onPdf={async () =>
        download(
          `${file}.pdf`,
          await zoneLabelsPdf(items, layout, publicUrl, signHint()),
          'application/pdf',
        )
      }
      onZpl={() => download(`${file}.zpl`, zoneLabelsZpl(items, publicUrl), 'text/plain')}
      onCsv={() => download(`${file}.csv`, zoneLabelsCsv(items, publicUrl), 'text/csv')}
      preview={
        items[0] && company ? (
          <Preview
            url={zoneLabelUrl(publicUrl, company.code, items[0].zoneId)}
            lines={
              <>
                <Typography variant="h6">{items[0].zoneName}</Typography>
                <Typography variant="body2">{items[0].siteName}</Typography>
                <Typography variant="caption">{company.name}</Typography>
              </>
            }
          />
        ) : null
      }
      onClose={onClose}
    >
      {zones.isLoading ? <Loading /> : null}
      {zones.error ? <LoadError error={zones.error} /> : null}
      {zones.data && !active.length ? (
        <Typography color="text.secondary">{t('labels.no_zones')}</Typography>
      ) : null}
      {active.length ? (
        <Stack direction="row" flexWrap="wrap" useFlexGap columnGap={2}>
          {active.map((z) => (
            <FormControlLabel
              key={z.id}
              control={
                <Checkbox
                  checked={!unticked.has(z.id)}
                  onChange={(e) =>
                    setUnticked((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.delete(z.id);
                      else next.add(z.id);
                      return next;
                    })
                  }
                />
              }
              label={z.name}
            />
          ))}
        </Stack>
      ) : null}
      {gaps.length ? (
        <Alert severity="warning">
          {t('labels.unprintable', { names: gaps.map((g) => g.zoneName).join(', ') })}
        </Alert>
      ) : null}
    </PrintDialog>
  );
}
